import React, { useState, useEffect } from 'react';
import { MultipleVideoPlayer as MultipleVideoPlayerView, MultipleVideoPlayerProps as MultipleVideoPlayerViewProps, VideoPlayerStreamProps } from 'components/views/organisms/MultipleVideoPlayer';
import { MultiplexedChannel } from 'infrastructure/t4/ChannelResolver/MultiplexedChannel';
import { useChannelStatus } from 'services/useChannelStatus';
import { useChannelPrepare } from 'services/useChannelStream';
import { useMultipleChannelStreams } from 'services/multipleChannels/useMultipleChannelStreams';
import { useSelectedStreamIndex } from 'services/multipleChannels/useSelectedStreamIndex';
import { usePublishRequest, PublishRequest } from 'services/usePublishRequest';
import { VideoPlayerContainerProps as SingleVideoPlayerContainerProps, VideoPlayer as SingleVideoPlayer, togglePlayback, useIsPlayingState, VideoPlaybackState } from '../VideoPlayer';

function useSelectedStreamProps(channel: MultiplexedChannel, options: { audio: boolean, streamSelector: 'v1' | 'v2', debug: boolean }) {
    const { audio, streamSelector, debug } = options;

    const [streamProps, setStreams] = useState<VideoPlayerStreamProps[]>();

    const streams = useMultipleChannelStreams(channel, audio);

    const selectedIndex = useSelectedStreamIndex(channel, { useV2: streamSelector === 'v2', debug });

    useEffect(() => {
        setStreams(streams.map((s, i) => ({
            id: i + 1,
            stream: s,
            visible: selectedIndex == i
        })));
    }, [selectedIndex, streams]);

    return streamProps;
}

export type MultipleVideoPlayerContainerProps = Omit<SingleVideoPlayerContainerProps, 'channel'> & {
    showAllStreams?: boolean;
    channel: MultiplexedChannel;
    streamSelector: 'v1' | 'v2';
    enableDebugLog: boolean;
}

export const MultipleVideoPlayer = (props: MultipleVideoPlayerContainerProps) => {
    const { channel, mirror, upsidedown, audio, viewOnly, projectId, environmentId, stretch, preserveAspectRatio, showAllStreams, streamSelector, enableDebugLog } = props;
    const { onPrepare } = props;

    const prepared = useChannelPrepare(channel);
    useEffect(() => {
        if (!onPrepare) return;
        onPrepare(prepared);
    }, [onPrepare, prepared]);

    const streamProps = useSelectedStreamProps(channel, { audio, streamSelector, debug: enableDebugLog });
    const isStarted = useChannelStatus(channel);
    const { isPlaying, playbackCallbacks } = useIsPlayingState();
    const [requesting, setRequesting] = usePublishRequest(channel.mediaDeviceId, projectId, environmentId);

    const [state, setState] = useState<VideoPlaybackState>('BLANK');
    useEffect(() => {
        if (isStarted === false) {
            if (requesting === PublishRequest.START) {
                setState('LOADING');
            } else {
                setState('PAUSING');
                setRequesting(undefined);
            }
        } else {
            if (requesting === PublishRequest.STOP) {
                setState('PAUSING');
            } else {
                if (isPlaying) {
                    setState('PLAYING');
                } else {
                    setState('LOADING');
                }
            }
        }
    }, [isPlaying, isStarted, requesting, setRequesting]);

    const _props: MultipleVideoPlayerViewProps = {
        streamProps,
        pause: state == 'PAUSING',
        loading: state == 'LOADING',
        stretch,
        preserveAspectRatio,
        mirror,
        upsidedown,
        showAllStreams,
        onClick: viewOnly
            ? undefined
            : () => setRequesting(togglePlayback(state)),
        ...playbackCallbacks,
    };

    return (
        <MultipleVideoPlayerView {..._props} />
    );
};

type VideoPlayerContainerProps = SingleVideoPlayerContainerProps | MultipleVideoPlayerContainerProps;

function isMultipleVideoPlayerContainerProps(props: VideoPlayerContainerProps): props is MultipleVideoPlayerContainerProps {
    return props.channel instanceof MultiplexedChannel;
}

export function VideoPlayer(props: VideoPlayerContainerProps) {
    return (isMultipleVideoPlayerContainerProps(props))
        ? <MultipleVideoPlayer {...props} />
        : <SingleVideoPlayer  {...props} />
        ;
}
